/** Accepting a figure that has no document — card C-E, **ADR-0067 decision 2A**.
 *
 * ADR-0036 permits a figure that rests on a stated basis and nothing else, and until this screen
 * such a figure entered every total and could never reach `VERIFIED`. This is the one place a
 * person accepts one. It is **not** a second door into document verification: a figure that has a
 * document shows no control here, and `AWAITING_REVIEW → VERIFIED` stays the only route into a
 * verified *document*.
 *
 * **Two sentences that must never be one.** *"Your role may not do this"* and *"you personally may
 * not, because you stated this basis"* are different facts with different remedies. The first is
 * about who you are in the workspace and is answered before any button exists; the second is about
 * this figure and **cannot be known in advance** — no read carries `basis_attested_by`, so it is
 * learned from the server's `attester_cannot_verify` and rendered as *separation of duties*, to an
 * Approver or Admin who plainly holds the role. Rendering it as a permissions message would send
 * them to an Admin to ask for a role they already have.
 *
 * **The server is the authority on every refusal.** Nothing here re-derives one: the screen decides
 * only what is knowable from what it has read (a document, a verifier, a superseded flag, the
 * person's role) so as not to offer a control that predictably fails, and renders the *coded*
 * refusal's meaning for everything else.
 */

import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { useMe } from "../api/auth";
import type { Activity } from "../api/queries";
import { Refused } from "../api/refusal";
import { useVerifyStatedBasis, verifyRefusalCode } from "../api/verifyStatedBasis";
import type { VerifyRefusalCode } from "../api/verifyStatedBasis";
import { VERIFY_REFUSALS } from "./RecordVerifyMeanings";
import { useRecordWork } from "../api/work";
import styles from "./Screen.module.css";
import own from "./Record.module.css";

/** Mirrored from `inventory.py`'s `MAY_VERIFY_STATED_BASIS` — the roles that may accept a document.
 * `test_accepting_a_documentless_figure_needs_the_roles_that_accept_a_document` is the backend half. */
const MAY_VERIFY: readonly string[] = ["admin", "approver"];

/** Codes after which pressing the button again can only fail the same way, so the control is not
 * offered again. `blocking_flag_open` is deliberately absent: the remedy is to resolve the flag,
 * after which this same press succeeds. */
const FINAL: readonly VerifyRefusalCode[] = [
  "attester_cannot_verify",
  "has_a_document",
  "no_stated_basis",
  "superseded",
  "already_verified",
  "no_such_record",
];

export function VerifyStatedBasis({ activity }: { activity: Activity }) {
  const me = useMe();
  const verify = useVerifyStatedBasis(activity.activity_record_id);

  const code = verifyRefusalCode(verify.error);

  // **What just happened comes first, and survives the refetch it causes.** Success and
  // `already_verified` both make the record page re-read, after which `verification_id` is set and
  // the checks below would hide this panel — taking the confirmation with it.
  if (verify.isSuccess) {
    return (
      <Panel title="Accepted" column="1 / 13">
        <p className={own.verifyOk} role="status" data-testid="verify-accepted">
          <strong className={own.verifyHead}>You accepted this figure.</strong>
          It is recorded once, against your account, and each figure priced from it now shows you as its
          verifier on its receipt. Nothing about the figure itself changed — only who has
          accepted it.
        </p>
      </Panel>
    );
  }
  if (code && FINAL.includes(code)) {
    return (
      <Panel title="Accepting this figure" column="1 / 13">
        <Refusal code={code} />
      </Panel>
    );
  }

  // **Nothing to offer** for a figure that is already accepted or no longer in force. The page's
  // own chips and note say so; a second sentence here would be the same fact twice.
  if (activity.verification_id || !activity.is_effective) return null;

  // **A figure with a document has no control here** — it goes through document review, and a
  // second door would let a reviewer skip the review that produces the figure.
  if (activity.source_document_id) {
    return (
      <Panel title="Accepting this figure" column="1 / 13">
        <p className={styles.note} data-testid="verify-has-document">
          This figure rests on a document, so it is accepted by reviewing that document rather than
          here.
        </p>
      </Panel>
    );
  }

  if (me.isPending) {
    return (
      <Panel title="Accepting this figure" column="1 / 13">
        <Empty>Loading…</Empty>
      </Panel>
    );
  }
  // **A failed read of who you are is not "you may not".** Saying so would be a claim about the
  // person's role produced by a dropped request.
  if (me.error) {
    return (
      <Panel title="Accepting this figure" column="1 / 13">
        <ReadFailed
          of="Your role in this workspace"
          error={me.error}
          onRetry={() => void me.refetch()}
        />
      </Panel>
    );
  }
  if (!me.data) return null;

  // **Your role may not** — said before any button exists (design rule 6), and it names who can.
  // This is the sentence for a Contributor, a Viewer and an Auditor. It is not the sentence for
  // somebody who holds the role and stated this basis; that one is `attester_cannot_verify`.
  if (!MAY_VERIFY.includes(me.data.role)) {
    return (
      <Panel title="Accepting this figure" column="1 / 13">
        <p className={styles.note} data-testid="verify-who-can">
          This figure has no document: it rests on a basis someone stated. An <strong>Admin</strong>{" "}
          or an <strong>Approver</strong> accepts it, and they cannot be the person who stated the
          basis. Your role cannot accept figures.
        </p>
      </Panel>
    );
  }

  const roleRefused =
    verify.error instanceof Refused && verify.error.isUnauthorized && code === null;

  return (
    <Panel
      title="Accept this figure"
      sub="no document · stated basis"
      column="1 / 13"
    >
      <p className={styles.note}>
        This figure rests on a basis someone stated, with no document behind it, so it is in the
        totals but is not accepted — and only an accepted figure can be filed. Accepting it puts your
        name on the receipt as the verifier. You cannot accept a basis you stated yourself.
      </p>
      <button
        type="button"
        className={styles.primary}
        data-testid="verify-accept"
        disabled={verify.isPending}
        onClick={() => verify.mutate()}
      >
        {verify.isPending ? "Accepting…" : "Accept this stated basis"}
      </button>
      {code === "blocking_flag_open" ? (
        <Refusal code={code} recordId={activity.activity_record_id} />
      ) : roleRefused ? (
        // The server disagrees with the role this page read (a role changed mid-session). Still
        // about who you are, so it is said in those terms and never as separation.
        <p className={own.verifyRefusal} role="alert" data-testid="verify-role-refused">
          <strong className={own.verifyHead}>Your role cannot accept figures.</strong>
          Only an Admin or an Approver can.
        </p>
      ) : code === null ? (
        <WriteFailed
          error={verify.error}
          of="this figure"
          onReload={() => window.location.reload()}
          onRetry={() => verify.mutate()}
          retrySafe
        />
      ) : null}
    </Panel>
  );
}

function Refusal({ code, recordId }: { code: VerifyRefusalCode; recordId?: string }) {
  const meaning = VERIFY_REFUSALS[code];
  return (
    <div
      className={own.verifyRefusal}
      role="alert"
      data-testid="verify-refused"
      data-code={code}
      data-kind={meaning.kind}
    >
      <strong className={own.verifyHead}>{meaning.head}</strong>
      {meaning.body}
      {recordId ? <OpenBlockingFlags recordId={recordId} /> : null}
    </div>
  );
}

/** The flags that are doing the blocking, **by name** — a count would send the reader looking. Read
 * from the record's work, which the page has already fetched (same query key). */
function OpenBlockingFlags({ recordId }: { recordId: string }) {
  const read = useRecordWork(recordId);
  if (read.error) {
    return (
      <span className={own.verifyFlags} data-testid="verify-flags-unread">
        Which flags could not be read just now; they are listed on the Workflow tab.
      </span>
    );
  }
  const open = (read.data?.flags ?? []).filter(
    (flag) => flag.state === "open" && flag.severity === "blocking",
  );
  if (open.length === 0) {
    return read.isPending ? null : (
      <span className={own.verifyFlags}>
        The Workflow tab lists the flags on this record.
      </span>
    );
  }
  return (
    <ul className={own.verifyFlags} data-testid="verify-blocking-flags">
      {open.map((flag) => (
        <li key={flag.id}>{flag.reason}</li>
      ))}
    </ul>
  );
}
